"use client";

// Libraries
import UserAvatar from "@/components/Avatar/UserAvatar";
import { signOut, useSession } from "next-auth/react";
import useInfluencerCampaign from "@/hooks/useInfluencerCampaign";
import { useCommerceSettings } from "@/contexts/CommerceSettingsContext";
import { useState, useEffect, useRef } from "react";
// `moment` et non `moment-timezone` : ce dernier embarque la base IANA
// complète, 724 745 octets bruts, dans le bundle partagé de toutes les pages,
// alors que les dates de notifications n'ont besoin d'aucun fuseau. Le seul
// endroit qui demandait un fuseau (la date de fin de promotion, en heure de
// Paris) le charge désormais à la demande, hors du chemin critique.
import moment from "moment";
import { toast, ToastContainer } from "react-toastify";
import Cookies from "universal-cookie";
import { useFeatureFlagVariantKey } from "posthog-js/react";

// Components
import { usePathname, useRouter, Link } from "@/i18n/navigation";
import { SpinnerCircular } from "spinners-react";
import Logo from "../Logo/Logo";
import PinkOctober from "@/components/ui/PinkOctober/PinkOctober";
import dynamic from "next/dynamic";
import Button from "../Button/Button";
import Image from "next/image";
import Icon from "../Icon/Icon";
import SideHeader from "../Header/SideHeader/SideHeader";
import Streak from "../Gamification/Streak/Streak";
import Fragments from "../Gamification/Fragments/Fragments";
import Keys from "../Gamification/Keys/Keys";
import Boost from "../Gamification/Boost/Boost";
import SolarIcon from "../SolarIcon/SolarIcon";
import BottomSheetModal from "@/components/ui/BottomSheetModal/BottomSheetModal";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import ClientSearchParamsHandler from "./ClientSearchParamsHandler";
import LandingHeader from "@/components/Landing/LandingHeader/LandingHeader";
import { isPublicMarketingPath } from "@/lib/publicPages";
import { ExtraPropProvider } from "@/contexts/ExtraPropContext";
import HeaderContextTitle from "@/components/ui/Header/HeaderContextTitle/HeaderContextTitle";
import { useMessengerNotifications } from "@/contexts/MessengerNotificationsContext";
import MessengerQuickAccess from "@/components/Messenger/MessengerQuickAccess";
import { usePromotionModalHold } from "@/contexts/PromotionModalContext";
import { useLocale, useTranslations } from "next-intl";
import { Suspense } from "react";
import { readLocal, writeLocal } from "@/helpers/safeStorage";

// Dynamic imports — ces composants sont rarement visibles au chargement initial
const SpeedSearch = dynamic(() => import("@/components/Algolia/SpeedSearch/SpeedSearch"), { ssr: false });
const PromotionModal = dynamic(() => import("../PromotionModal/PromotionModal"), { ssr: false });
const SuspendedModal = dynamic(() => import("@/components/Teams/SuspendedModal").then(mod => ({ default: mod.SuspendedModal })), { ssr: false });
// LandingHeader est importé statiquement (et non en ssr: false) : c'est la
// seule navigation des pages vitrine, elle doit être dans le HTML servi aux
// moteurs de recherche et visible au premier affichage, sans clignotement.
// [Temporaire — à retirer après le 19/04/2026]
const Lottie = dynamic(() => import("@/components/ui/LottiePlayer/LottiePlayer"), { ssr: false });

export default function UserLayout(props) {
    // Variables
    const t = useTranslations("layout");
    const tLeagues = useTranslations("leagues");
    const tNotif = useTranslations("notifications");
    const { data: session, status, update: updateSession } = useSession();
    // Seulement tant qu'il n'y a pas encore de session : une relecture (update()
    // de NextAuth, après un coffre) repasse aussi par « loading » et ne doit pas
    // remplacer la barre du haut et le menu par leurs squelettes.
    const loading = status === "loading" && !session;
    const router = useRouter();
    const pathname = usePathname();
    const cookies = new Cookies();
    const scrollContainerRef = useRef(null);
    const locale = useLocale();
    const variant = useFeatureFlagVariantKey("landing-page-style");
    const { unreadCount: messengerUnreadCount } = useMessengerNotifications();

    // Promotion : la fenêtre (début, fin, interrupteur) est calculée par le
    // serveur et arrive par le contexte, alimenté au montage par
    // /api/commerce/settings. Le bandeau apparaît donc une fraction de seconde
    // après la peinture, exactement comme avant (où l'on attendait le
    // chargement de moment-timezone), mais sans les 724 745 octets de la
    // bibliothèque de fuseaux ni la moindre arithmétique de dates ici.
    const promoConfigRef = useRef(null);
    const { settings: commerceSettings, settled: commerceSettled } =
        useCommerceSettings();
    const activePromotion = commerceSettings.promotion;

    const RAW_PROMO_CONFIG = (() => {
        promoConfigRef.current = {
            isActive: activePromotion !== null,
            discount: activePromotion ? activePromotion.discountPercent : null,
            nameFr: activePromotion ? activePromotion.nameFr : null,
            nameEn: activePromotion ? activePromotion.nameEn : null,
            endDate: activePromotion ? activePromotion.endsAt : null,
            text: t("UserLayout_promo_text"),
            buttonText: t("UserLayout_promo_button"),
            fallbackText: t("UserLayout_promo_fallback"),
        };
        return promoConfigRef.current;
    })();

    // Campagne influenceur : quand elle est active pour ce visiteur, TOUTE la
    // communication promo flash Believemy est masquée (bandeau, modale, bouton
    // stylisé de la sidebar), même si la promo est plus avantageuse que
    // l'offre du créateur. Décision produit du 04/07/2026.
    const { campaign: influencerCampaign, campaignChecked: influencerChecked } =
        useInfluencerCampaign(!!session?.user);
    const PROMO_CONFIG = influencerCampaign
        ? { ...RAW_PROMO_CONFIG, isActive: false }
        : RAW_PROMO_CONFIG;

    // States
    const [notifications, setNotifications] = useState([]);
    const [notificationsAreLoading, setNotificationsAreLoading] =
        useState(false);
    const [notificationsPanelIsOpen, setNotificationsPanelIsOpen] =
        useState(false);
    const [notificationIsDeleting, setNotificationIsDeleting] = useState(false);
    const [sendib, setSendIB] = useState(false);
    const [sideIsOpen, setSideIsOpen] = useState(
        variant === "test" && !session ? false : true,
    );
    const [menuIsOpen, setMenuIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showSuspendedModal, setShowSuspendedModal] = useState(false);

    const [sideNavIsOpen, setSideNavIsOpen] = useState(false);
    const [updatingMustLeaveNavOpen, setUpdatingMustLeaveNavOpen] =
        useState(false);
    const [page, setPage] = useState("other");
    const [maintenance, setMaintenance] = useState(null);
    const [promotionModalIsOpen, setPromotionModalIsOpen] = useState(false);

    // Une seule sollicitation à la fois. La modale de promotion parle en
    // premier : tant qu'elle est à l'écran, ou qu'elle peut encore y surgir
    // parce que le verdict du serveur n'est pas tombé, la carte du changelog se
    // tait. Sans ce verrou les deux s'affichaient ensemble et se disputaient le
    // même clic. Les conditions lues ici sont exactement celles de l'effet qui
    // ouvre la modale, moins celles qui dépendent du réseau : on retient donc
    // l'écran par défaut pour un compte susceptible de voir la promotion, et on
    // le rend dès que la réponse dit qu'il n'y en a pas.
    const promotionModalCanStillAppear =
        !!session?.user &&
        !session.user.pro &&
        !session.user.currentTeam &&
        cookies.get("promotionModalShown") !== new Date().toDateString();

    usePromotionModalHold(
        promotionModalIsOpen ||
            (promotionModalCanStillAppear &&
                (!(commerceSettled && influencerChecked) ||
                    PROMO_CONFIG.isActive)),
    );

    // Détection si on est dans une session de formation ou d'accélérateur
    const isCourseSession =
        /\/course\/[^/]+\/[^/]+\/[^/]+/.test(pathname) ||
        /\/accelerators\/[^/]+\/[^/]+\/[^/]+\/[^/]+/.test(pathname);

    // Préférence utilisateur : garder la sidebar ouverte en formation
    const userWantsNavOnCourses = session?.user?.showNavOnCourses ?? false;

    // Détection du mode lecture (articles, termes de glossaire, pages promo, formations, accélérateurs)
    const isReadingMode =
        pathname.includes("/r/") ||
        /^\/glossaries\/[^/]+\/[^/]+/.test(pathname) ||
        pathname.includes("/code-promo/") ||
        pathname.includes("/discount-code/") ||
        pathname.startsWith("/accelerators/rocket") ||
        pathname.startsWith("/accelerators/python") ||
        pathname.startsWith("/join-instructors") ||
        pathname.startsWith("/join-mentors") ||
        (isCourseSession && !userWantsNavOnCourses);

    // Détection du mode admin (les panels admin et pédagogique ont leur propre sidebar)
    const isAdminMode =
        pathname.startsWith("/admin") || pathname.startsWith("/pedagogy");

    // État temporaire pour la sidebar en mode lecture (non persisté)
    const [sidebarOpenInReadingMode, setSidebarOpenInReadingMode] = useState(false);

    // [Temporaire — à retirer après le 19/04/2026]
    // Notification première visite : sidebar rétractée en formation
    const [showSidebarHint, setShowSidebarHint] = useState(false);
    const [bebotAnimation, setBebotAnimation] = useState(null);

    useEffect(() => {
        const EXPIRY = new Date("2026-04-19").getTime();
        if (Date.now() > EXPIRY) return;
        if (!isCourseSession) return;
        if (readLocal("sidebar-hint-dismissed")) return;

        setShowSidebarHint(true);
        import("@/public/static/animations/bebot.json").then((mod) => {
            setBebotAnimation(mod.default);
        });
    }, [isCourseSession]);

    // État effectif de la sidebar
    const effectiveSideIsOpen = isReadingMode ? sidebarOpenInReadingMode : sideIsOpen;

    // Helpers pour piloter la sidebar (toggle factorisé : l'API Symfony toggle déjà, donc on garde-fou sur l'état actuel pour ne pas désynchroniser)
    const toggleSidebar = () => {
        if (isReadingMode) {
            setSidebarOpenInReadingMode(!sidebarOpenInReadingMode);
        } else {
            setSideIsOpen(!sideIsOpen);
            updateMustLeaveNavOpen();
        }
    };
    const closeSidebar = () => {
        if (effectiveSideIsOpen) toggleSidebar();
    };
    const openSidebar = () => {
        if (!effectiveSideIsOpen) toggleSidebar();
    };

    // Methods
    const handleMaintenanceChange = (maintenanceValue) => {
        setMaintenance(maintenanceValue);
    };
    const loadNotifications = async (markAsRead = false) => {
        if (!notificationsAreLoading) {
            setNotificationsAreLoading(true);
            const response = await fetch("/api/user/get/notifications", {
                method: "post",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ markAsRead }),
            });

            const fetchedData = await response.json();

            if (!response.ok) {
                setNotificationsAreLoading(false);
                toast.error(fetchedData.message);
            } else {
                setNotificationsAreLoading(false);
                setNotifications(fetchedData.notifications);

                if (markAsRead) {
                    const event = new Event("visibilitychange");
                    document.dispatchEvent(event);
                }
            }
        }
    };
    const onDeleteNotificationHandler = async (notificationId) => {
        if (!notificationIsDeleting) {
            setNotificationIsDeleting(true);
            const response = await fetch("/api/user/delete/notification", {
                method: "post",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    notificationId,
                }),
            });

            const fetchedData = await response.json();

            if (!response.ok) {
                setNotificationIsDeleting(false);
                toast.error(fetchedData.message);
            } else {
                setNotificationIsDeleting(false);
                setNotifications(
                    notifications.filter(
                        (notification) => notification.id !== notificationId,
                    ),
                );
            }
        }
    };
    const onSignOutClickedHandler = () => {
        toast.success(
            t("UserLayout_bye", { firstName: session.user.firstName }),
        );

        // if actual page is /articles or /r/:slug
        if (
            pathname == "/articles" ||
            pathname == "/r/[slug]" ||
            pathname == "/courses" ||
            pathname == "/course/[slug]"
        ) {
            signOut({ redirect: false });
        } else {
            let tmpUrl = process.env.NEXTAUTH_URL;
            if (locale !== "fr") {
                tmpUrl = `${tmpUrl}/${locale}`;
            }
            signOut({ callbackUrl: tmpUrl });
        }
    };
    const onToggleDarkMode = async () => {
        // Temporarily update session
        session.user.darkMode = !session.user.darkMode;

        if (!isLoading) {
            setIsLoading(true);

            const response = await fetch("/api/user/toggle-dark-mode", {
                method: "get",
                headers: {
                    "Content-Type": "application/json",
                },
            });

            const fetchedData = await response.json();

            if (!response.ok) {
                setIsLoading(false);
                toast.error(fetchedData.message);
            } else {
                setIsLoading(false);
                const event = new Event("visibilitychange");
                document.dispatchEvent(event);
            }

            setIsLoading(false);
        }
    };
    const updateMustLeaveNavOpen = async () => {
        if (!updatingMustLeaveNavOpen && session && session.user) {
            setUpdatingMustLeaveNavOpen(true);
            const response = await fetch("/api/user/must-leave-nav-open", {
                method: "get",
                headers: {
                    "Content-Type": "application/json",
                },
            });

            const fetchedData = await response.json();

            if (!response.ok) {
                setUpdatingMustLeaveNavOpen(false);
                toast.error(fetchedData.message);
            } else {
                setUpdatingMustLeaveNavOpen(false);
            }
        }
    };
    const detectPage = (pathname) => {
        if (pathname == "/") {
            setPage("dashboard");
        } else if (pathname.includes("/reading-list")) {
            setPage("reading-list");
        } else if (pathname.includes("/leagues")) {
            setPage("leagues");
        } else if (pathname.includes("/u/") || pathname.includes("/@")) {
            setPage("profile");
        } else if (
            pathname.includes("/courses") ||
            pathname.includes("/course")
        ) {
            setPage("courses");
        } else if (pathname == "/articles" || pathname.startsWith("/articles/") || pathname.includes("/r/")) {
            setPage("articles");
        } else if (pathname.includes("/reps")) {
            setPage("reps");
        } else if (pathname.includes("/accelerators")) {
            setPage("accelerators");
        } else if (pathname.includes("/mentoring")) {
            setPage("mentors");
        } else if (
            pathname == "/glossaries" ||
            pathname == "/glossaries/[slug]" ||
            pathname == "/glossaries/[slug]/[termSlug]"
        ) {
            setPage("glossaries");
        } else if (pathname.includes("/community/discussion/")) {
            setPage("community");
        } else if (pathname.includes("/pricing")) {
            setPage("pricing");
        } else if (pathname.includes("/teams")) {
            setPage("teams");
        } else if (pathname.includes("/glossaries")) {
            setPage("glossaries");
        } else if (pathname === "/tools" || pathname.startsWith("/tools/")) {
            // /outils, la page d'un outil et « Votre situation » : l'entrée
            // « Outils » de la barre latérale reste allumée.
            setPage("tools");
        } else {
            setPage("other");
        }
    };
    const closeAllOpenPanels = () => {
        setNotificationsPanelIsOpen(false);
        setMenuIsOpen(false);
    };

    useEffect(() => {
        let notifTimer;
        if (!props.onlyUnconnected && session && session.user) {
            // Déférer le chargement des notifications pour laisser React finir le premier rendu
            notifTimer = setTimeout(() => loadNotifications(), 100);
        }
        detectPage(pathname);
        return () => { if (notifTimer) clearTimeout(notifTimer); };
    }, []);

    useEffect(() => {
        if (session && session.user) {
            setSendIB(true);
            if (!session.user.hasVerifiedEmail) {
                // Jamais pendant le tunnel de paiement (/pricing) : la
                // vérification attend la fin du checkout, le garde rattrape
                // l'utilisateur sur /courses ou /reps juste après le paiement
                // (même exception que la redirection onboarding ci-dessous)
                if (
                    !pathname.includes("/verify-account") &&
                    !pathname.includes("/pricing")
                ) {
                    router.push("/verify-account", {
                        locale: session.user.locale || "fr",
                    });
                }
                return;
            }
            // Appliquer la préférence utilisateur pour la sidebar
            setSideIsOpen(session.user.mustLeaveNavOpen ?? true);
        }
    }, [session]);

    useEffect(() => {
        detectPage(pathname);
        closeAllOpenPanels();

        // Reset l'état temporaire de la sidebar en mode lecture à chaque changement de page
        setSidebarOpenInReadingMode(false);
    }, [pathname]);

    // Retour de paiement (?subscription_started=1) : le flag d'abonnement est
    // posé en base par le webhook Stripe quelques secondes APRÈS la
    // redirection. On rafraîchit la session à petites doses jusqu'à ce qu'il
    // arrive, pour que toute l'interface (badge Devenir Pro, verrous) bascule
    // sans rechargement manuel. Le paramètre est capturé AU PREMIER RENDU :
    // les trackers enfants (SubscriptionConversionTracker, RepsPathClient)
    // nettoient l'URL avant que nos effets ne tournent.
    const [subscriptionJustStarted] = useState(
        () =>
            typeof window !== "undefined" &&
            new URLSearchParams(window.location.search).get(
                "subscription_started",
            ) === "1",
    );
    const subscriptionRefreshTriesRef = useRef(0);
    useEffect(() => {
        if (!subscriptionJustStarted) return;
        if (!session?.user) return; // session pas encore chargée
        // À jour (pro pour Pro/Founder, essential pour Plus) : on s'arrête
        if (session.user.pro || session.user.essential || session.user.ultimate)
            return;
        if (subscriptionRefreshTriesRef.current >= 10) return;

        const delay = subscriptionRefreshTriesRef.current === 0 ? 800 : 2500;
        const timer = setTimeout(async () => {
            subscriptionRefreshTriesRef.current += 1;
            try {
                await updateSession?.();
            } catch (error) {
                // Réessaie au tick suivant
            }
        }, delay);
        return () => clearTimeout(timer);
    }, [session, subscriptionJustStarted, updateSession]);

    // Gestion de la redirection vers l'onboarding
    useEffect(() => {
        if (session && !loading && session.user && !session.user.isOnboarded) {
            if (
                !pathname.includes("/pricing") &&
                !pathname.includes("onboarding") &&
                !pathname.includes("/verify-account")
            ) {
                router.push("/onboarding", {
                    locale: locale || "fr",
                });
            }
        }
    }, [session, loading, pathname, router]);

    // Gestion de l'affichage automatique de la modale de promotion
    useEffect(() => {
        if (PROMO_CONFIG.isActive && session?.user && !session?.user?.pro && !session?.user?.currentTeam) {
            // Vérifier si la modale a déjà été vue aujourd'hui
            const promotionShownToday = cookies.get("promotionModalShown");
            const today = new Date().toDateString();

            if (promotionShownToday !== today) {
                // Délai de 2 secondes avant d'afficher la modale pour une meilleure UX
                const timer = setTimeout(() => {
                    setPromotionModalIsOpen(true);
                }, 2000);

                return () => clearTimeout(timer);
            }
        }
        // `cookies` est volontairement hors des dépendances : l'objet est
        // reconstruit à chaque rendu, l'y laisser annulait et relançait le
        // compte à rebours de deux secondes à chaque rendu, au point de
        // retarder indéfiniment la modale sur une page qui rend souvent.
    }, [session, PROMO_CONFIG.isActive]);

    // Raccourci clavier Cmd+/ (ou Ctrl+/) pour toggle la sidebar — ignore les champs éditables pour éviter de hijacker la frappe
    useEffect(() => {
        const handler = (e) => {
            if (!(e.metaKey || e.ctrlKey) || e.key !== "/") return;
            const target = e.target;
            const tag = target?.tagName;
            if (target?.isContentEditable || tag === "INPUT" || tag === "TEXTAREA") return;
            e.preventDefault();
            toggleSidebar();
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [sideIsOpen, sidebarOpenInReadingMode, isReadingMode]);

    // Gestionnaire de fermeture de la modale de promotion
    const handlePromotionModalClose = () => {
        setPromotionModalIsOpen(false);
        // Marquer que la modale a été vue aujourd'hui
        const today = new Date().toDateString();
        const expires = new Date();
        expires.setHours(23, 59, 59, 999); // Expire à la fin de la journée
        cookies.set("promotionModalShown", today, { path: "/", expires });
    };

    if (process.env.MAINTENANCE == "true") {
        let isPass = maintenance == "ironman";

        if (isPass) {
            // Le laissez-passer vaut pour tout le site : sans path explicite,
            // le navigateur rattache le cookie au répertoire de la page où on
            // l'a posé (/fr/reps, /fr/pricing...) et il faudrait redonner
            // ?maintenance=ironman à chaque section. maxAge le fait aussi
            // survivre à la fermeture du navigateur, le temps d'un
            // déploiement.
            cookies.set("maintenance", "ironman", {
                path: "/",
                maxAge: 60 * 60 * 24,
            });
        }

        if (cookies.get("maintenance") !== "ironman") {
            return (
                <div className="relative flex flex-col min-h-screen justify-end items-center overflow-hidden">
                    <Suspense fallback={null}>
                        <ClientSearchParamsHandler
                            onMaintenanceChange={handleMaintenanceChange}
                        />
                    </Suspense>
                    {/* Image de fond complète */}
                    <Image
                        src="/images/bebot/maintenance.jpg"
                        alt="BeBot prépare le spectacle"
                        fill
                        className="object-cover object-center"
                        priority
                    />

                    {/* Overlay pour le texte en bas */}
                    <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>

                    {/* Contenu en bas */}
                    <div className="relative z-10 text-center px-6 pb-12 lg:pb-16">
                        <div className="mb-3 flex justify-center opacity-50">
                            <Icon ratio="0.018" white />
                        </div>
                        <h1 className="lg:text-4xl text-2xl font-bold font-sourceSans text-white tracking-tight drop-shadow-lg">
                            {t("maintenance_title")}
                        </h1>
                        <p className="mt-2 text-white/60 font-sourceSans text-base max-w-md mx-auto leading-relaxed mb-0">
                            {t("maintenance_subtitle")}
                        </p>
                    </div>
                </div>
            );
        }
    }

    // Fonction pour obtenir le texte de notification selon le format
    const getNotificationText = (notification) => {
        // Nouveau format avec type et données

        if (notification.notificationType) {
            // Transformer "league.week_end.top3" en "notifications.league.week_end.top3"
            const translationKey = `${notification.notificationType}`;

            // Préparer les données avec gestion des positions ordinales
            let notificationData = { ...notification.notificationData };

            // Traduire le nom de la league si leagueLevel est fourni
            if (notification.notificationData.leagueLevel) {
                try {
                    notificationData.league = tLeagues(
                        `${notification.notificationData.leagueLevel}`,
                    );
                } catch (e) {
                    // Garder le nom original si la traduction échoue
                }
            }

            // Gérer l'affichage ordinal des positions pour le top 3
            if (
                notification.notificationType.includes("top3") &&
                notificationData.position
            ) {
                const position = notificationData.position;
                if (locale === "fr") {
                    notificationData.position =
                        position === 1 ? "1er" : `${position}e`;
                } else {
                    notificationData.position =
                        position === 1
                            ? "1st"
                            : position === 2
                            ? "2nd"
                            : position === 3
                            ? "3rd"
                            : `${position}th`;
                }
            }

            // Vérifier si la clé de traduction existe
            try {
                const translatedText = tNotif(translationKey, notificationData);
                // Si la traduction retourne la clé elle-même, c'est qu'elle n'existe pas
                if (translatedText !== translationKey) {
                    return translatedText;
                }
            } catch (e) {
                // En cas d'erreur, on continue avec le fallback
            }

            // Fallback au message si la traduction n'existe pas
            return notification.message || "Nouvelle notification";
        }

        // Ancien format - afficher le message directement
        return notification.message;
    };

    const notificationsPanel = (
        <div className="bg-white dark:bg-bgDark-840 w-80 md:w-[420px] max-h-[32rem] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-5 py-4 border-b border-gray-100 dark:border-borderDark flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-base">
                        {t("notifications_title") || "Notifications"}
                    </h3>
                    {notifications.filter((n) => !n.seen).length > 0 && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded-full">
                            {notifications.filter((n) => !n.seen).length}
                        </span>
                    )}
                </div>
            </div>

            {/* Content */}
            <div className="overflow-y-auto flex-1 scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-grayDark-700">
                {/* If notifications are loading */}
                {notificationsAreLoading && !notifications[0] && (
                    <div className="flex justify-center items-center py-16">
                        <SpinnerCircular
                            size={36}
                            thickness={180}
                            color="#C4A461"
                        />
                    </div>
                )}

                {/* Empty state */}
                {!notificationsAreLoading && notifications.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-gray-50 to-gray-100 dark:from-grayDark-700 dark:to-grayDark-600 flex items-center justify-center mb-4 shadow-sm">
                            <svg
                                className="w-7 h-7 text-gray-400 dark:text-grayDark-400"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.5}
                                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                                />
                            </svg>
                        </div>
                        <p className="text-sm font-medium text-gray-600 dark:text-grayDark-300 mb-1">
                            {t("notifications_empty") || "Aucune notification"}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-grayDark-500 mb-0">
                            {t("notifications_empty_hint") ||
                                "Vous êtes à jour !"}
                        </p>
                    </div>
                )}

                {/* Notifications list */}
                {notifications.map((notification, index) => (
                    <Link
                        href={notification.link || "/"}
                        key={notification.id}
                        className="block"
                    >
                        <div
                            className={`px-5 py-4 group cursor-pointer transition-all duration-150 relative ${
                                !notification.seen
                                    ? "bg-gradient-to-r from-primary/5 to-transparent dark:from-primary/10 dark:to-transparent"
                                    : "hover:bg-gray-50 dark:hover:bg-grayDark-700/30"
                            } ${
                                index !== notifications.length - 1
                                    ? "border-b border-gray-100 dark:border-grayDark-700/30"
                                    : ""
                            }`}
                        >
                            <div className="flex gap-3">
                                {/* Icon / Avatar */}
                                <div
                                    className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                                        !notification.seen
                                            ? "bg-primary/10 dark:bg-primary/20"
                                            : "bg-gray-100 dark:bg-grayDark-700"
                                    }`}
                                >
                                    <svg
                                        className={`w-5 h-5 ${
                                            !notification.seen
                                                ? "text-primary"
                                                : "text-gray-500 dark:text-grayDark-400"
                                        }`}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={1.5}
                                            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                                        />
                                    </svg>
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                    <div
                                        className={`text-sm leading-relaxed ${
                                            !notification.seen
                                                ? "text-gray-900 dark:text-white font-medium"
                                                : "text-gray-600 dark:text-grayDark-300"
                                        }`}
                                    >
                                        {getNotificationText(notification)}
                                    </div>

                                    {/* Date */}
                                    <div className="flex items-center gap-2 mt-1.5">
                                        <svg
                                            className="w-3.5 h-3.5 text-gray-400 dark:text-grayDark-500"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                            />
                                        </svg>
                                        <span className="text-xs text-gray-400 dark:text-grayDark-500">
                                            {moment(notification.dateCreation)
                                                .locale(locale)
                                                .calendar()[0]
                                                .toUpperCase() +
                                                moment(
                                                    notification.dateCreation,
                                                )
                                                    .locale(locale)
                                                    .calendar()
                                                    .slice(1)}
                                        </span>
                                    </div>
                                </div>

                                {/* Delete button */}
                                <button
                                    className="flex-shrink-0 self-start p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-150 hover:bg-red-50 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onDeleteNotificationHandler(
                                            notification.id,
                                        );
                                    }}
                                >
                                    {notificationIsDeleting ? (
                                        <SpinnerCircular
                                            size={16}
                                            thickness={180}
                                            speed={100}
                                            color="#ef4444"
                                        />
                                    ) : (
                                        <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M6 18L18 6M6 6l12 12"
                                            />
                                        </svg>
                                    )}
                                </button>
                            </div>

                            {/* Unread dot indicator */}
                            {!notification.seen && (
                                <div className="absolute top-4 left-1 w-2 h-2 rounded-full bg-primary shadow-sm shadow-primary/50"></div>
                            )}
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );

    if (
        loading &&
        props.needAuth &&
        (pathname.includes("/u/") || pathname.includes("/@"))
    ) {
        return <></>;
    }

    let links = null;
    if (session) {
        links = (
            <ul>
                {session &&
                    session.user &&
                    session.user.roles.includes("ROLE_SUPER_ADMIN") && (
                        <Link
                            href="/admin"
                            className="text-[#06050F]/60 dark:text-white hover:text-white"
                        >
                            <li className="dropdown-item flex items-center gap-3">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-4 w-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                                    />
                                </svg>
                                {t("userDropdown_administrationPanel")}
                            </li>
                        </Link>
                    )}
                {(session.user.roles.includes("ROLE_SUPER_ADMIN") ||
                    session.user.roles.includes("ROLE_ADMIN") ||
                    session.user.roles.includes("ROLE_INSTRUCTOR")) && (
                    <Link
                        href="/instructor"
                        className="text-[#06050F]/60 dark:text-white hover:text-white"
                    >
                        <li className="dropdown-item flex items-center gap-3">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-4 h-4"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    fill="currentColor"
                                    fillRule="evenodd"
                                    d="M18.059 4.708a2.25 2.25 0 0 0-2.166-1.25a2.952 2.952 0 0 0-.51.096l-.446 3.74l3.118-.836a8.175 8.175 0 0 0 .289-.083a.25.25 0 0 0 .137-.238a8.315 8.315 0 0 0-.073-.292c-.19-.708-.26-.955-.35-1.137Zm-4.683 3.003l.447-3.743l-4.719 1.264l-.446 3.743l4.718-1.264ZM7.098 9.394l.446-3.743c-.817.219-1.401.379-1.85.544c-.474.174-.728.33-.904.505c-.279.28-.48.628-.582 1.009c-.064.24-.072.537.014 1.034c.063.362.167.79.31 1.338l2.566-.687ZM3.25 11.093c-.226-.843-.408-1.526-.506-2.094c-.104-.6-.129-1.141.015-1.678a3.75 3.75 0 0 1 .97-1.681c.394-.393.875-.643 1.447-.853c.554-.203 1.259-.392 2.132-.626l.78-.21a.746.746 0 0 1 .286-.076l5.972-1.6l.021-.006a.746.746 0 0 1 .286-.076c.474-.126.821-.21 1.142-.232a3.75 3.75 0 0 1 3.609 2.084c.164.332.272.736.43 1.328l.023.084l.012.046c.048.176.097.359.109.534a1.75 1.75 0 0 1-.973 1.684a2.826 2.826 0 0 1-.562.186L9.697 10.25h6.355c.898 0 1.648 0 2.242.08c.628.084 1.195.27 1.65.725c.456.456.642 1.023.726 1.65c.08.595.08 1.345.08 2.243v1.107c0 1.367 0 2.47-.117 3.337c-.12.9-.38 1.658-.982 2.26c-.602.601-1.36.86-2.26.981c-.866.117-1.969.117-3.336.117h-4.11c-1.368 0-2.47 0-3.337-.117c-.9-.12-1.658-.38-2.26-.981c-.602-.602-.86-1.36-.982-2.26c-.116-.867-.116-1.97-.116-3.337v-4.962Zm1.5.657V16c0 1.435.001 2.436.103 3.192c.099.734.28 1.122.556 1.399c.277.277.665.457 1.399.556c.755.101 1.756.103 3.192.103h4c1.435 0 2.436-.002 3.192-.103c.733-.099 1.122-.28 1.399-.556c.277-.277.457-.665.556-1.4c.101-.755.103-1.756.103-3.191v-1c0-.964-.002-1.612-.067-2.095c-.062-.461-.169-.659-.3-.789c-.13-.13-.327-.237-.789-.3c-.483-.065-1.13-.066-2.094-.066H4.75Zm8.448 2.83l.047.034l.042.03c.361.259.7.501.94.738c.27.265.523.623.523 1.118c0 .495-.252.852-.522 1.118c-.241.237-.58.48-.94.738l-.043.03l-.047.034c-.361.26-.706.506-1.001.652c-.315.155-.834.321-1.33-.029c-.43-.304-.533-.795-.574-1.138c-.043-.358-.043-.826-.043-1.358v-.094c0-.533 0-1 .043-1.358c.04-.343.144-.834.575-1.138c.495-.35 1.014-.185 1.329-.03c.295.146.64.394 1.001.653Zm-1.429.832c-.018.25-.02.593-.02 1.088c0 .495.002.838.02 1.088c.149-.097.34-.234.602-.421c.418-.3.658-.474.805-.619a.62.62 0 0 0 .046-.048a1.009 1.009 0 0 0-.046-.048c-.147-.145-.387-.32-.805-.619a18.297 18.297 0 0 0-.602-.421Z"
                                    clipRule="evenodd"
                                ></path>
                            </svg>
                            {t("userDropdown_instructorPanel")}
                        </li>
                    </Link>
                )}
                {(session.user.roles.includes("ROLE_SUPER_ADMIN") ||
                    session.user.roles.includes("ROLE_ADMIN") ||
                    session.user.roles.includes("ROLE_MENTOR")) && (
                    <Link
                        href="/mentor"
                        className="text-[#06050F]/60 dark:text-white hover:text-white"
                    >
                        <li className="dropdown-item flex items-center gap-3">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
                                />
                            </svg>
                            {t("userDropdown_mentorPanel")}
                        </li>
                    </Link>
                )}
                {(session.user.roles.includes("ROLE_SUPER_ADMIN") ||
                    session.user.roles.includes("ROLE_ADMIN") ||
                    session.user.roles.includes("ROLE_PEDAGOGY")) && (
                    <Link
                        href="/pedagogy"
                        className="text-[#06050F]/60 dark:text-white hover:text-white"
                    >
                        <li className="dropdown-item flex items-center gap-3">
                            <SolarIcon
                                name="clipboard-text-linear"
                                className="w-4 h-4"
                            />
                            {t("userDropdown_pedagogyPanel")}
                        </li>
                    </Link>
                )}

                {session.user.hasVerifiedEmail &&
                    (session.user.roles.includes("ROLE_SUPER_ADMIN") ||
                        session.user.roles.includes("ROLE_ADMIN")) && (
                        <Link
                            href="/writer"
                            className="text-[#06050F]/60 dark:text-white hover:text-white"
                        >
                            <li className="dropdown-item flex items-center gap-3">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="w-4 h-4"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        fill="currentColor"
                                        fillRule="evenodd"
                                        d="M11.943 1.25h.114c2.309 0 4.118 0 5.53.19c1.444.194 2.584.6 3.479 1.494c.793.793 1.203 1.78 1.42 3.005c.215 1.203.254 2.7.262 4.558a.75.75 0 0 1-1.5.006a73.32 73.32 0 0 0-.025-1.753H7.011a.73.73 0 0 1-.024 0h-4.21A120.56 120.56 0 0 0 2.75 12c0 2.378.002 4.086.176 5.386c.172 1.279.5 2.05 1.069 2.62c.484.484 1.112.79 2.067.978c.976.19 2.235.246 3.944.26a.75.75 0 1 1-.012 1.5c-1.704-.014-3.092-.067-4.22-.288c-1.15-.226-2.084-.634-2.84-1.39c-.895-.895-1.3-2.035-1.494-3.48c-.19-1.411-.19-3.22-.19-5.529v-.114c0-2.309 0-4.118.19-5.53c.194-1.444.6-2.584 1.494-3.479c.895-.895 2.035-1.3 3.48-1.494c1.411-.19 3.22-.19 5.529-.19Zm-9.086 6h3.731l2.856-4.488c-1.127.017-2.052.06-2.83.164c-1.279.172-2.05.5-2.62 1.069c-.569.57-.896 1.34-1.068 2.619c-.027.202-.05.413-.07.636Zm8.35-4.5a.753.753 0 0 1-.074.153L8.366 7.25h4.722l2.83-4.448C14.857 2.75 13.576 2.75 12 2.75h-.793Zm6.391.207L14.866 7.25h6.277c-.033-.382-.076-.73-.133-1.048c-.184-1.036-.498-1.7-1.005-2.207c-.537-.538-1.254-.86-2.407-1.038Zm.85 10.031a2.52 2.52 0 1 1 3.564 3.563l-4.282 4.282a6.38 6.38 0 0 1-.572.532a3.698 3.698 0 0 1-.683.423c-.206.098-.422.17-.732.273l-1.878.626a1.227 1.227 0 0 1-1.552-1.552l.626-1.878c.103-.31.175-.526.273-.732c.116-.242.258-.472.423-.683c.14-.18.301-.341.532-.572l4.282-4.282Zm2.503 1.06a1.02 1.02 0 0 0-1.442 0l-.131.132l.016.05c.082.236.238.548.533.843a2.223 2.223 0 0 0 .893.55l.131-.132a1.02 1.02 0 0 0 0-1.442Zm-1.265 2.708a3.756 3.756 0 0 1-1.442-1.442L15.258 18.3c-.272.273-.364.366-.44.464a2.193 2.193 0 0 0-.252.406c-.053.113-.096.236-.218.601l-.345 1.037l.189.19l1.037-.347c.365-.121.488-.164.6-.217a2.21 2.21 0 0 0 .407-.252c.098-.076.191-.168.464-.44l2.986-2.986Z"
                                        clipRule="evenodd"
                                    ></path>
                                </svg>
                                {t("userDropdown_writerPanel")}
                            </li>
                        </Link>
                    )}
                <Link
                    href="/affiliate"
                    className="text-[#06050F]/60 dark:text-white hover:text-white"
                >
                    <li className="dropdown-item flex items-center gap-3">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                        >
                            <path
                                fillRule="evenodd"
                                d="M11.943 1.25c-2.309 0-4.118 0-5.53.19c-1.444.194-2.584.6-3.479 1.494c-.895.895-1.3 2.035-1.494 3.48c-.19 1.411-.19 3.22-.19 5.529v.114c0 2.309 0 4.118.19 5.53c.194 1.444.6 2.584 1.494 3.479c.895.895 2.035 1.3 3.48 1.494c1.411.19 3.22.19 5.529.19h.114c2.309 0 4.118 0 5.53-.19c1.444-.194 2.584-.6 3.479-1.494c.895-.895 1.3-2.035 1.494-3.48c.19-1.411.19-3.22.19-5.529v-.114c0-2.309 0-4.118-.19-5.53c-.194-1.444-.6-2.584-1.494-3.479c-.895-.895-2.035-1.3-3.48-1.494c-1.411-.19-3.22-.19-5.529-.19zm-.693 1.5c-1.994.002-3.477.02-4.636.176c-1.279.172-2.05.5-2.62 1.069c-.569.57-.896 1.34-1.068 2.619c-.155 1.159-.174 2.642-.176 4.636h4.164a4 4 0 0 1-.756-1.527C5.619 7.57 7.57 5.619 9.723 6.158a4 4 0 0 1 1.527.756zm-8.5 10c.002 1.994.02 3.477.176 4.636c.172 1.279.5 2.05 1.069 2.62c.57.569 1.34.896 2.619 1.068c1.159.155 2.642.174 4.636.176v-7.126A4.75 4.75 0 0 1 7 16.75a.75.75 0 0 1 0-1.5a3.25 3.25 0 0 0 3.163-2.5zm10 8.5c1.994-.002 3.477-.02 4.636-.176c1.279-.172 2.05-.5 2.62-1.069c.569-.57.896-1.34 1.068-2.619c.155-1.159.174-2.642.176-4.636h-7.413A3.25 3.25 0 0 0 17 15.25a.75.75 0 0 1 0 1.5a4.75 4.75 0 0 1-4.25-2.626zm8.5-10h-4.164a4 4 0 0 0 .757-1.527c.538-2.153-1.413-4.104-3.565-3.565a4 4 0 0 0-1.528.756V2.75c1.994.002 3.477.02 4.636.176c1.279.172 2.05.5 2.62 1.069c.569.57.896 1.34 1.068 2.619c.155 1.159.174 2.642.176 4.636m-8.5 0v-1.22a2.5 2.5 0 0 1 1.891-2.417a1.44 1.44 0 0 1 1.746 1.746a2.5 2.5 0 0 1-2.415 1.891zm-1.5-1.223a2.5 2.5 0 0 0-1.891-2.414a1.44 1.44 0 0 0-1.746 1.746a2.5 2.5 0 0 0 2.415 1.891h1.222z"
                                clipRule="evenodd"
                            />
                        </svg>
                        {t("userDropdown_affiliate")}
                    </li>
                </Link>
                <Link
                    href="/account"
                    className="text-[#06050F]/60 dark:text-white hover:text-white"
                >
                    <li className="dropdown-item flex items-center gap-3">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                            />
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                        </svg>
                        {t("userDropdown_account")}
                    </li>
                </Link>
                {session && session.user && session.user.darkMode ? (
                    <li
                        onClick={onToggleDarkMode}
                        className="dropdown-item flex items-center gap-3 text-[#06050F]/60 dark:text-white hover:text-white"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                            />
                        </svg>
                        {t("userDropdown_lightMode")}
                    </li>
                ) : (
                    <li
                        onClick={onToggleDarkMode}
                        className="dropdown-item flex items-center gap-3 text-[#06050F]/60 dark:text-white hover:text-white"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                            />
                        </svg>
                        {t("userDropdown_darkMode")}
                    </li>
                )}
                <li
                    onClick={onSignOutClickedHandler}
                    className="dropdown-item flex items-center gap-3 text-[#06050F]/60 dark:text-white hover:text-white"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                        />
                    </svg>
                    {t("userDropdown_logout")}
                </li>
            </ul>
        );
    }

    if (pathname.includes("/series/[slug]/[seasonNumber]/[episodeNumber]")) {
        return (
            <>
                {/* <ToastContainer
                    position="bottom-right"
                    autoClose={8000}
                    theme="dark"
                    closeOnClick={true}
                /> */}
                {props.children}
            </>
        );
    }

    if (/^\/workshops\/[^/]+\/play/.test(pathname)) {
        return <>{props.children}</>;
    }

    if (pathname === "/links") {
        return <>{props.children}</>;
    }

    if (/^\/code\/[^/]+/.test(pathname)) {
        return <>{props.children}</>;
    }

    if (/^\/accelerators\/[^/]+\/admission/.test(pathname)) {
        return <>{props.children}</>;
    }

    if (/^\/accelerators\/[^/]+\/notebook/.test(pathname)) {
        return <>{props.children}</>;
    }

    if (pathname === "/press") {
        return <>{props.children}</>;
    }

    // Page d'aide (/aide, /support) : page autonome, sans chrome. C'est l'URL
    // d'assistance déclarée à l'App Store pour Reps : le header propose /pricing
    // aux visiteurs déconnectés, et on ne met pas une page de vente sous les yeux
    // d'un reviewer venu chercher un support.
    if (pathname === "/support") {
        return <>{props.children}</>;
    }

    // Page de prospection (/rdv/...) : page autonome, sans aucune chrome (ni sidebar ni header).
    if (pathname.startsWith("/rdv")) {
        return <>{props.children}</>;
    }

    // Page d'appel vue par un INVITÉ (sans compte) : page autonome, sans chrome
    // (sidebar/header). Un utilisateur connecté garde son interface habituelle.
    if (
        (pathname.startsWith("/call/") || pathname.startsWith("/appel/")) &&
        !session
    ) {
        return <>{props.children}</>;
    }

    // Invoice
    if (pathname.includes("/account/invoice")) {
        return (
            <>
                {/* <ToastContainer
                    position="bottom-right"
                    autoClose={8000}
                    theme="dark"
                    closeOnClick={true}
                /> */}
                {props.children}
            </>
        );
    }

    // Documents
    if (
        pathname.includes("/documents") ||
        pathname.includes("/entry-document")
    ) {
        return (
            <>
                {/* <ToastContainer
                    position="bottom-right"
                    autoClose={8000}
                    theme="dark"
                    closeOnClick={true}
                /> */}
                {props.children}
            </>
        );
    }

    // Login and Sign up
    if (
        pathname.includes("/login") ||
        pathname.includes("/signup") ||
        pathname.includes("/verify-account") ||
        pathname.includes("/onboarding") ||
        pathname.includes("/security/forgotten-password")
    ) {
        return (
            <>
                {/* <ToastContainer
                    position="bottom-right"
                    autoClose={8000}
                    theme="dark"
                    closeOnClick={true}
                /> */}
                {props.children}
            </>
        );
    }

    // Verification
    if (pathname.includes("/verified")) {
        return (
            <>
                {/* <ToastContainer
                    position="bottom-right"
                    autoClose={8000}
                    theme="dark"
                    closeOnClick={true}
                /> */}
                {props.children}
            </>
        );
    }

    // Rocket
    if (pathname == "/accelerators/rocket" && !session) {
        return (
            <>
                {/* <ToastContainer
                    position="bottom-right"
                    autoClose={8000}
                    theme="dark"
                    closeOnClick={true}
                /> */}
                {props.children}
            </>
        );
    }

    // Python accelerator (no sidebar for non-logged users only)
    if (pathname == "/accelerators/python" && !session) {
        return (
            <>
                {/* <ToastContainer
                    position="bottom-right"
                    autoClose={8000}
                    theme="dark"
                    closeOnClick={true}
                /> */}
                {props.children}
            </>
        );
    }

    // Invitation
    if (pathname.includes("/invitations")) {
        return (
            <>
                {/* <ToastContainer
                    position="bottom-right"
                    autoClose={8000}
                    theme="dark"
                    closeOnClick={true}
                /> */}
                {props.children}
            </>
        );
    }

    // Landing page (marketing layout without sidebar for non-logged users)
    if (pathname === "/" && !session) {
        return <>{props.children}</>;
    }

    // Pages vitrine (tarifs, formations, mentorat, articles...) : un visiteur
    // sans compte garde la barre de navigation de la page d'accueil au lieu de
    // basculer dans l'interface de l'application, qui lui montre une barre
    // latérale conçue pour des membres et lui retire le bouton d'inscription
    // permanent. La liste des chemins vit dans lib/publicPages.js.
    //
    // Un membre connecté n'entre pas ici : il conserve son interface habituelle.
    //
    // data-chrome="vitrine" laisse une page savoir qu'elle est sous cet
    // en-tête flottant : un bandeau coloré peut alors remonter derrière lui
    // au lieu de laisser les 80 pixels du padding en blanc (voir /tools).
    if (isPublicMarketingPath(pathname) && !session) {
        return (
            <>
                <LandingHeader />
                <div className="pt-20" data-chrome="vitrine">
                    {props.children}
                </div>
            </>
        );
    }

    return (
        <div className="relative">
            <Suspense fallback={null}>
                <ClientSearchParamsHandler
                    onMaintenanceChange={handleMaintenanceChange}
                />
            </Suspense>
            {/* <ToastContainer
                position="bottom-right"
                autoClose={8000}
                theme="dark"
                closeOnClick={true}
            /> */}
            <div
                className={`flex flex-col h-screen overflow-hidden [&.dark_.react-loading-skeleton]:[--base-color:#2C3A46] [&.dark_.react-loading-skeleton]:[--highlight-color:#38444D] ${
                    session && session.user && session.user.darkMode && "dark"
                }`}
            >
                {/* Side (masqué en mode admin — le panel admin a sa propre sidebar) */}
                {!isAdminMode && (
                    <div
                        className={`lg:hidden block fixed inset-0 transition-opacity duration-300 ease-in-out ${
                            sideNavIsOpen
                                ? "bg-black/50 pointer-events-auto cursor-pointer"
                                : "bg-black/0 pointer-events-none"
                        }`}
                        style={{ zIndex: 1000 }}
                        onClick={() => setSideNavIsOpen(false)}
                    >
                        <div
                            className={`fixed top-0 left-0 bottom-0 h-full transition-transform duration-300 ease-out bg-white dark:bg-bgDark-500 cursor-auto ${
                                sideNavIsOpen ? "translate-x-0" : "-translate-x-full"
                            }`}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                boxShadow:
                                    "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)",
                            }}
                        >
                            <SideHeader
                                page={page}
                                session={session}
                                sideNavIsOpen={sideNavIsOpen}
                                promoConfig={PROMO_CONFIG}
                            />
                        </div>
                    </div>
                )}
                {/* Bannière temporaire pour donner son avis sur la nouvelle interface */}
                {/* <section className="h-11">
                    <div className="bg-black flex justify-center items-center h-11 text-white text-sm">
                        <div className="text-red-500">Nouvelle interface</div>
                        <div className="mx-3 text-gray-800">|</div>
                        <div>
                            <a
                                href="https://forms.gle/9z8xQ1Q7Jy1jQ9Jk7"
                                target="_blank"
                                className="underline text-white"
                            >
                                Donnez votre avis
                            </a>
                            , merci !
                        </div>
                        <div className="absolute right-3 text-gray-500 hover:text-white duration-100">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="1em"
                                height="1em"
                                className="cursor-pointer"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    fill="none"
                                    stroke="currentColor"
                                    strokeLinecap="round"
                                    strokeWidth="2"
                                    d="M20 20L4 4m16 0L4 20"
                                ></path>
                            </svg>
                        </div>
                    </div>
                </section> */}
                <div className="flex w-full relative h-full">
                    {/* Nav (masqué en mode admin) — quand repliée, la sidebar passe en mode "rail" (icônes uniquement) que ce soit en mode normal OU en mode lecture, pour rester accessible partout */}
                    {!isAdminMode && (
                        <div
                            className={`h-full lg:block hidden transition-all duration-[400ms] ease-sidebar ${
                                effectiveSideIsOpen
                                    ? "w-[230px] ml-0 opacity-100"
                                    : "w-[58px] ml-0 opacity-100"
                            }`}
                        >
                            <SideHeader
                                page={page}
                                session={session}
                                sessionLoading={loading}
                                promoConfig={PROMO_CONFIG}
                                onCollapse={closeSidebar}
                                onExpand={openSidebar}
                                compact={!effectiveSideIsOpen}
                            />
                        </div>
                    )}

                    {/* Main */}
                    <div className="relative flex-1 overflow-hidden max-w-full h-full">
                        <div className="font-geistSans relative h-full">
                            <div className="bg-white dark:bg-bgDark-700 transition-all duration-150 flex flex-col h-full">
                                <div
                                    className="overflow-x-hidden overflow-y-auto flex-1"
                                    ref={scrollContainerRef}
                                >
                                    {/* Header */}
                                    {!props.withoutHeader && (
                                        <div
                                            className={`sticky top-0 z-50 h-[65px]`}
                                        >
                                            <div
                                                className={`relative max-w-full z-50`}
                                            >
                                                <header
                                                    className={`transition-all duration-150 sticky background-blur-white dark:!bg-bgDark-300/70 h-[65px] left-0 top-0 right-0 z-0`}
                                                >
                                                    <div
                                                        className={`flex items-center justify-between z-40 transition-all duration-1000 pt-3 px-3 dark:text-white`}
                                                    >
                                                        {/* Search and category */}
                                                        <div className="flex items-center gap-3 min-w-0 flex-1">
                                                            {/* Button for mobiles (masqué en mode admin) */}
                                                            {!isAdminMode && (
                                                                <div
                                                                    onClick={() => {
                                                                        setSideNavIsOpen(
                                                                            !sideNavIsOpen,
                                                                        );
                                                                        updateMustLeaveNavOpen();
                                                                    }}
                                                                    className="hover:text-primary cursor-pointer duration-100 lg:hidden block shrink-0"
                                                                >
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        className="w-5 h-5"
                                                                        viewBox="0 0 24 24"
                                                                    >
                                                                        <path
                                                                            fill="currentColor"
                                                                            fillRule="evenodd"
                                                                            d="M20.75 7a.75.75 0 0 1-.75.75H4a.75.75 0 0 1 0-1.5h16a.75.75 0 0 1 .75.75Zm0 5a.75.75 0 0 1-.75.75H4a.75.75 0 0 1 0-1.5h16a.75.75 0 0 1 .75.75Zm0 5a.75.75 0 0 1-.75.75H4a.75.75 0 0 1 0-1.5h16a.75.75 0 0 1 .75.75Z"
                                                                            clipRule="evenodd"
                                                                        ></path>
                                                                    </svg>
                                                                </div>
                                                            )}
                                                            {/* <Link href="/search" style={{ color: "inherit" }}> */}
                                                            <div className="md:hidden flex items-center gap-1 shrink-0">
                                                                <Link href="/">
                                                                    <Logo
                                                                        icon
                                                                    />
                                                                </Link>
                                                                <PinkOctober />
                                                            </div>
                                                            {isAdminMode && (
                                                                <div className="md:block hidden">
                                                                    <Link href="/" className="hover:opacity-80 transition-opacity">
                                                                        <Logo icon />
                                                                    </Link>
                                                                </div>
                                                            )}
                                                            <div className="md:block hidden shrink-0">
                                                                <SpeedSearch />
                                                            </div>

                                                            {/* Titre de contexte posé par la page en
                                                                cours (le lecteur de formation nomme
                                                                ainsi sa formation). Aucune page qui
                                                                n'en pose n'est touchée. */}
                                                            <HeaderContextTitle />

                                                            {/* </Link> */}
                                                            {props.optionalMenu &&
                                                                props.optionalMenu}
                                                        </div>

                                                        {/* Buttons */}
                                                        {loading ? (
                                                            <>
                                                                <Skeleton
                                                                    width={100}
                                                                    height={30}
                                                                />
                                                            </>
                                                        ) : (
                                                            <>
                                                                {session ? (
                                                                    <div className="flex items-center space-x-3 z-50 shrink-0">
                                                                        {/* Booster d'XP : hors capsule, et seulement
                                                                            quand on en a un (actif ou en réserve).
                                                                            Il multiplie tout l'XP du site, il se
                                                                            montre donc partout, pas seulement sur
                                                                            /reps où il était enfermé. */}
                                                                        <Boost
                                                                            session={session}
                                                                        />

                                                                        {/* Capsule Série + Fragments + Clés */}
                                                                        <div className="md:flex hidden items-center bg-gray-50/80 dark:bg-bgDark-700 border border-gray-200/70 dark:border-bgDark-300 rounded-full">
                                                                            <Streak
                                                                                session={session}
                                                                                hoverRounded="rounded-l-full rounded-r-none"
                                                                            />
                                                                            <div className="w-px h-3.5 bg-gray-200 dark:bg-bgDark-300 self-center" />
                                                                            <Fragments
                                                                                session={session}
                                                                                compact={true}
                                                                                hoverRounded={
                                                                                    session?.user?.repsOnWeb
                                                                                        ? "rounded-none"
                                                                                        : "rounded-r-full rounded-l-none"
                                                                                }
                                                                            />
                                                                            {/* Clés Reps : seulement pour les comptes qui
                                                                                gardent Reps sur le site */}
                                                                            {session?.user?.repsOnWeb && (
                                                                                <>
                                                                                    <div className="w-px h-3.5 bg-gray-200 dark:bg-bgDark-300 self-center" />
                                                                                    <Keys
                                                                                        session={session}
                                                                                        hoverRounded="rounded-r-full rounded-l-none"
                                                                                    />
                                                                                </>
                                                                            )}
                                                                        </div>

                                                                        {/* Inviter & gagner (icone seule) */}
                                                                        <Link
                                                                            href="/affiliate"
                                                                            title={t("topbar_inviteEarn")}
                                                                            aria-label={t("topbar_inviteEarn")}
                                                                            className="group md:flex hidden items-center justify-center w-9 h-9 rounded-full hover:bg-gray-100 dark:hover:bg-bgDark-700 duration-150"
                                                                        >
                                                                            <SolarIcon
                                                                                name="gift-linear"
                                                                                className="w-[22px] h-[22px] text-gray-400 dark:text-grayDark-300 group-hover:text-primary duration-150"
                                                                            />
                                                                        </Link>

                                                                        {/* Recherche mobile */}
                                                                        <div className="md:hidden block">
                                                                            <SpeedSearch />
                                                                        </div>

                                                                        {/* Messages */}
                                                                        <div className="relative">
                                                                            <MessengerQuickAccess
                                                                                onOpen={() => {
                                                                                    setNotificationsPanelIsOpen(false);
                                                                                    setMenuIsOpen(false);
                                                                                }}
                                                                                className="group flex items-center justify-center w-9 h-9 rounded-full hover:bg-gray-100 dark:hover:bg-bgDark-700 duration-150 text-gray-400 dark:text-grayDark-300"
                                                                            >
                                                                                {messengerUnreadCount > 0 && (
                                                                                    <div className="absolute -top-1 -right-1 text-xs text-white bg-red-400 rounded-full w-5 h-5 flex justify-center items-center z-10">
                                                                                        {messengerUnreadCount}
                                                                                    </div>
                                                                                )}
                                                                                <SolarIcon
                                                                                    name="chat-round-line-linear"
                                                                                    className="w-[22px] h-[22px] group-hover:text-primary duration-150"
                                                                                />
                                                                            </MessengerQuickAccess>
                                                                        </div>

                                                                        {/* Notifications */}
                                                                        <div className="relative">
                                                                            {session.user.unreadedNotifications > 0 && (
                                                                                <div className="absolute -top-1 -right-1 text-xs text-white bg-red-400 rounded-full w-5 h-5 flex justify-center items-center z-10">
                                                                                    {session.user.unreadedNotifications}
                                                                                </div>
                                                                            )}
                                                                            <div
                                                                                className={`group flex items-center justify-center w-9 h-9 rounded-full duration-150 cursor-pointer ${
                                                                                    notificationsPanelIsOpen
                                                                                        ? "bg-gray-100 dark:bg-bgDark-700"
                                                                                        : "hover:bg-gray-100 dark:hover:bg-bgDark-700"
                                                                                }`}
                                                                                onClick={() => {
                                                                                    if (!notificationsPanelIsOpen) {
                                                                                        loadNotifications(true);
                                                                                    }
                                                                                    setNotificationsPanelIsOpen(!notificationsPanelIsOpen);
                                                                                    setMenuIsOpen(false);
                                                                                }}
                                                                            >
                                                                                <SolarIcon
                                                                                    name="bell-linear"
                                                                                    className={`w-[22px] h-[22px] duration-150 ${
                                                                                        notificationsPanelIsOpen
                                                                                            ? "text-primary"
                                                                                            : "text-gray-400 dark:text-grayDark-300 group-hover:text-primary"
                                                                                    }`}
                                                                                />
                                                                            </div>
                                                                            {notificationsPanelIsOpen && (
                                                                                <div className="absolute top-full mt-3 z-[100] -right-[76px] md:-right-3 animate-in fade-in slide-in-from-top-2 duration-200">
                                                                                    {/* Fleche pointant sur la cloche */}
                                                                                    <div className="absolute -top-[7px] right-[86px] md:right-[22px] w-4 h-4 bg-white dark:bg-bgDark-840 border-l border-t border-gray-100 dark:border-borderDark rotate-45 rounded-tl-[3px] z-10 [clip-path:polygon(0_0,100%_0,0_100%)]"></div>
                                                                                    {/* Panneau */}
                                                                                    <div className="relative rounded-2xl shadow-xl shadow-black/10 dark:shadow-black/30 border border-gray-100 dark:border-borderDark overflow-hidden">
                                                                                        {notificationsPanel}
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </div>

                                                                        {/* Premium (seul accent colore) */}
                                                                        {!session.user.currentTeam && !session.user.pro && !session.user.essential && (
                                                                            <>
                                                                                <div className="border-l h-5 w-px lg:block hidden border-gray-200/70 dark:border-bgDark-300"></div>
                                                                                <Link
                                                                                    href="/pricing"
                                                                                    className="relative group lg:block hidden"
                                                                                >
                                                                                    {PROMO_CONFIG.isActive && (
                                                                                        <div className="absolute -top-2 -right-2 z-10 bg-tertiary text-white text-xs font-bold px-2 py-0.5 rounded-full shadow-lg transform rotate-12">
                                                                                            -{PROMO_CONFIG.discount}%
                                                                                        </div>
                                                                                    )}
                                                                                    <div className="px-4 py-2 rounded-full text-sm font-medium bg-primary text-white hover:bg-primary/90 duration-150 whitespace-nowrap btn-3d lip-orange">
                                                                                        {PROMO_CONFIG.isActive
                                                                                            ? PROMO_CONFIG.buttonText
                                                                                            : PROMO_CONFIG.fallbackText}
                                                                                    </div>
                                                                                    {PROMO_CONFIG.isActive && (
                                                                                        <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                                                                                            <div className="bg-slate-800 dark:bg-grayDark-700 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap relative">
                                                                                                <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-slate-800 dark:border-b-grayDark-700"></div>
                                                                                                {PROMO_CONFIG.text}
                                                                                            </div>
                                                                                        </div>
                                                                                    )}
                                                                                </Link>
                                                                            </>
                                                                        )}

                                                                        {/* Avatar */}
                                                                        <div
                                                                            className="ml-2 flex items-center gap-1"
                                                                            style={{ lineHeight: 0 }}
                                                                        >
                                                                            <div>
                                                                                <UserAvatar
                                                                                    user={session.user}
                                                                                    alt={session.user.firstName}
                                                                                    width={44}
                                                                                    height={44}
                                                                                    className="rounded-full object-cover cursor-pointer"
                                                                                    onClick={() => {
                                                                                        setMenuIsOpen(!menuIsOpen);
                                                                                        setNotificationsPanelIsOpen(false);
                                                                                    }}
                                                                                    style={{ width: 44, height: 44 }}
                                                                                />
                                                                            </div>
                                                                            <svg
                                                                                xmlns="http://www.w3.org/2000/svg"
                                                                                className={`h-4 w-4 text-gray-400 dark:text-grayDark-400 cursor-pointer`}
                                                                                fill="none"
                                                                                viewBox="0 0 24 24"
                                                                                stroke="currentColor"
                                                                                onClick={() => {
                                                                                    setMenuIsOpen(!menuIsOpen);
                                                                                    setNotificationsPanelIsOpen(false);
                                                                                }}
                                                                            >
                                                                                {menuIsOpen ? (
                                                                                    <path
                                                                                        fill="currentColor"
                                                                                        fillRule="evenodd"
                                                                                        d="M11.512 8.43a.75.75 0 0 1 .976 0l7 6a.75.75 0 1 1-.976 1.14L12 9.987l-6.512 5.581a.75.75 0 1 1-.976-1.138l7-6Z"
                                                                                        clipRule="evenodd"
                                                                                    ></path>
                                                                                ) : (
                                                                                    <path
                                                                                        strokeLinecap="round"
                                                                                        strokeLinejoin="round"
                                                                                        strokeWidth={2}
                                                                                        d="M19 9l-7 7-7-7"
                                                                                    />
                                                                                )}
                                                                            </svg>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <div className="flex items-center gap-1.5 shrink-0">
                                                                        <Link href="/pricing">
                                                                            <div className="text-sm text-black/70 md:block hidden font-semibold dark:text-white/70 p-1.5 hover:bg-gray-50 rounded duration-100">
                                                                                Believemy Pro
                                                                            </div>
                                                                        </Link>
                                                                        <div className="border-l mx-3 h-5 w-[1px] md:block hidden"></div>
                                                                        <Link href="/login">
                                                                            <div className="text-sm text-black/70 font-semibold dark:text-white/70 p-1.5 hover:bg-gray-50 rounded duration-100">
                                                                                {t(
                                                                                    "UserLayout_login",
                                                                                )}
                                                                            </div>
                                                                        </Link>
                                                                        <Link href="/signup">
                                                                            <Button
                                                                                type="pop"
                                                                                small
                                                                            >
                                                                                {t(
                                                                                    "UserLayout_signup",
                                                                                )}
                                                                            </Button>
                                                                        </Link>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                    {menuIsOpen && (
                                                        <div className="absolute right-3 text-sm z-[100] mt-3 animate-in fade-in slide-in-from-top-2 duration-200">
                                                            {/* Arrow */}
                                                            <div className="absolute -top-[7px] right-[34px] w-4 h-4 bg-white dark:bg-bgDark-840 border-l border-t border-gray-100 dark:border-borderDark rotate-45 rounded-tl-[3px] z-10 [clip-path:polygon(0_0,100%_0,0_100%)]"></div>
                                                            {/* Panel */}
                                                            <div className="relative rounded-xl bg-white dark:bg-bgDark-840 text-black dark:text-white w-52 overflow-hidden shadow-xl shadow-black/10 dark:shadow-black/30 border border-gray-100 dark:border-borderDark">
                                                                {links}
                                                            </div>
                                                        </div>
                                                    )}
                                                </header>
                                            </div>
                                        </div>
                                    )}
                                    {/* Content */}

                                    {/* [Temporaire — à retirer après le 19/04/2026] Modale sidebar rétractée en formation */}
                                    <BottomSheetModal
                                        isOpen={showSidebarHint && !!bebotAnimation}
                                        onClose={() => {
                                            setShowSidebarHint(false);
                                            writeLocal("sidebar-hint-dismissed", "1");
                                        }}
                                        maxWidthClass="sm:max-w-sm"
                                        zIndexClass="z-50"
                                    >
                                        <div className="p-6 text-center">
                                            <div className="mx-auto -mt-16 mb-2 w-[180px]">
                                                <Lottie
                                                    src={bebotAnimation}
                                                    loop
                                                    autoplay
                                                    style={{ width: 180, height: 180 }}
                                                />
                                            </div>
                                            <p className="mb-1 text-lg font-semibold text-gray-900 dark:text-white">
                                                Nouveau
                                            </p>
                                            <p className="text-sm text-gray-600 dark:text-grayDark-300 !mb-0">
                                                La barre latérale se ferme automatiquement pour vous offrir plus d&apos;espace pendant vos formations. Vous pouvez la rouvrir à tout moment via le menu.
                                            </p>
                                            <button
                                                onClick={() => {
                                                    setShowSidebarHint(false);
                                                    writeLocal("sidebar-hint-dismissed", "1");
                                                }}
                                                className="mt-5 w-full rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary/90 btn-3d lip-orange"
                                            >
                                                Compris !
                                            </button>
                                            <Link
                                                href="/account"
                                                onClick={() => {
                                                    setShowSidebarHint(false);
                                                    writeLocal("sidebar-hint-dismissed", "1");
                                                }}
                                                className="mt-3 inline-block text-xs text-gray-400 hover:text-gray-600 dark:text-grayDark-500 dark:hover:text-grayDark-300 transition-colors"
                                            >
                                                Modifier dans les paramètres
                                            </Link>
                                        </div>
                                    </BottomSheetModal>

                                    <ExtraPropProvider
                                        extraProp={scrollContainerRef}
                                    >
                                        {props.children}
                                    </ExtraPropProvider>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <ToastContainer
                position="bottom-right"
                autoClose={8000}
                theme="dark"
                closeOnClick={true}
            />

            {/* Modale de promotion */}
            <PromotionModal
                open={promotionModalIsOpen}
                onClose={handlePromotionModalClose}
                endDate={PROMO_CONFIG.endDate}
                userSession={session}
                promotionName={locale === "en" ? PROMO_CONFIG.nameEn : PROMO_CONFIG.nameFr}
            />

            {/* Modale membre suspendu */}
            <SuspendedModal
                isOpen={showSuspendedModal}
                onClose={() => setShowSuspendedModal(false)}
                teamName={session?.user?.currentTeam?.name}
                teamId={session?.user?.currentTeam?.id}
            />
        </div>
    );
}
